import { useState, useEffect, useContext } from "react";
import { Row, Col, Select, Button, Spin } from "antd";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import {
  FileProtectOutlined, BarChartOutlined, TeamOutlined,
  MoneyCollectOutlined, BranchesOutlined, CalendarOutlined,
  SafetyCertificateOutlined, ApartmentOutlined, ReloadOutlined,
  CheckCircleOutlined, ClockCircleOutlined, UsergroupAddOutlined,
  ManOutlined, WomanOutlined, ScheduleOutlined, DollarOutlined, RiseOutlined,
} from "@ant-design/icons";

import { useNavigation } from "../../context/NavigationContext";
import TotalEmployees               from "../../components/HrDashboard/TotalEmployees";
import AttendanceReport             from "../../components/HrDashboard/AttendanceReport";
import LeaveUtilization             from "../../components/HrDashboard/LeaveUtilization";
import OrganisationLeave            from "../../components/HrDashboard/OrganisationLeave";
import PayrollSummary               from "../../components/HrDashboard/PayrollSummary";
import DepartmentWiseSalary         from "../../components/HrDashboard/DepartmentWiseSalary";
import StatutoryCompliance          from "../../components/HrDashboard/StatutoryCompliance";
import HrAnalytics                  from "../../components/HrDashboard/HrAnalytics";
import EmployeeAttendance           from "../../components/HrDashboard/EmployeeAttendance";
import OtChart                      from "../../components/HrDashboard/OtChart";
import LopChart                     from "../../components/HrDashboard/LopChart";
import DepartmentAttendanceBenchmark from "../../components/HrDashboard/DepartmentAttendanceBenchmark";
import OrgChartList                 from "./OrgChartList";

const { Option } = Select;
const PRIMARY = "#0e497b";

/* ══════════════════════════════════════════════════════════════
   KPI CARD — Executive Horizon Hero Card (Zero Empty Space)
══════════════════════════════════════════════════════════════ */
function KpiCard({
  accent, icon, label, bigValue, loading,
  s1Val, s1Label, s1Color, s1Icon,
  s2Val, s2Label, s2Color, s2Icon,
}) {
  return (
    <div
      style={{
        background: `linear-gradient(145deg, #ffffff 0%, ${accent}06 100%)`,
        borderRadius: 15,
        border: `1px solid ${accent}28`,
        boxShadow: `0 4px 14px ${accent}12`,
        padding: "14px 16px 12px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%",
        transition: "all 0.22s ease-in-out",
        position: "relative",
        overflow: "hidden",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.boxShadow = `0 8px 24px ${accent}22`;
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.borderColor = `${accent}50`;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = `0 4px 14px ${accent}12`;
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.borderColor = `${accent}28`;
      }}
    >
      {/* Top Accent Strip */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0,
        height: 3.5, background: `linear-gradient(90deg, ${accent}, ${accent}88)`,
      }} />

      {/* Top Hero Section */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4, marginBottom: 12 }}>
        <div>
          {/* Label Tag */}
          <span style={{
            fontSize: 10, fontWeight: 800, color: accent,
            textTransform: "uppercase", letterSpacing: "0.08em",
            background: `${accent}12`, border: `1px solid ${accent}20`,
            padding: "2.5px 8px", borderRadius: 12,
            display: "inline-block", marginBottom: 6,
          }}>
            {label}
          </span>
          {/* Big Hero Number — Vibrant Color, 32px */}
          <div style={{
            fontSize: 32, fontWeight: 900, color: accent,
            lineHeight: 1, letterSpacing: "-0.03em",
          }}>
            {loading ? <Spin size="small" /> : bigValue}
          </div>
        </div>

        {/* Floating 3D Icon Box */}
        <div style={{
          width: 42, height: 42, borderRadius: 12,
          background: `linear-gradient(135deg, ${accent}, ${accent}d0)`,
          color: "#ffffff", fontSize: 20, flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: `0 4px 14px ${accent}40`,
        }}>
          {icon}
        </div>
      </div>

      {/* Bottom Sub-stats Bar */}
      {(s1Label || s2Label) && (
        <div style={{
          display: "grid", gridTemplateColumns: (s1Label && s2Label) ? "1fr 1fr" : "1fr", gap: 8,
          paddingTop: 10, borderTop: `1px solid ${accent}18`,
        }}>
          {/* Stat 1 */}
          {s1Label && (
            <div style={{
              background: `${s1Color}0c`,
              border: `1px solid ${s1Color}25`,
              borderRadius: 9,
              padding: "6px 9px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
              minWidth: 0,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0, overflow: "hidden" }}>
                <span style={{ color: s1Color, fontSize: 14, display: "flex", alignItems: "center", flexShrink: 0 }}>{s1Icon}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: s1Color, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s1Label}</span>
              </div>
              <span style={{ fontSize: 13.5, fontWeight: 800, color: s1Color, flexShrink: 0, marginLeft: 4 }}>
                {loading ? "—" : s1Val}
              </span>
            </div>
          )}

          {/* Stat 2 */}
          {s2Label && (
            <div style={{
              background: `${s2Color}0c`,
              border: `1px solid ${s2Color}25`,
              borderRadius: 9,
              padding: "6px 9px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
              minWidth: 0,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0, overflow: "hidden" }}>
                <span style={{ color: s2Color, fontSize: 14, display: "flex", alignItems: "center", flexShrink: 0 }}>{s2Icon}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: s2Color, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s2Label}</span>
              </div>
              <span style={{ fontSize: 13.5, fontWeight: 800, color: s2Color, flexShrink: 0, marginLeft: 4 }}>
                {loading ? "—" : s2Val}
              </span>
            </div>
          )}
        </div>
      )}

    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   QUICK ACTION BUTTON
══════════════════════════════════════════════════════════════ */
function QuickBtn({ icon, label, color, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "6px 14px", borderRadius: 8,
        background: "#ffffff", border: "1px solid #e2e8f0",
        color: "#475569", fontWeight: 600, fontSize: 12,
        cursor: "pointer", outline: "none", transition: "all 0.18s ease", whiteSpace: "nowrap",
        boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = color;
        e.currentTarget.style.color = color;
        e.currentTarget.style.background = `${color}0d`;
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = "#e2e8f0";
        e.currentTarget.style.color = "#475569";
        e.currentTarget.style.background = "#ffffff";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      <span style={{ fontSize: 13, color: color, display: "flex", alignItems: "center" }}>{icon}</span>
      {label}
    </button>
  );
}

/* ══════════════════════════════════════════════════════════════
   SECTION HEADER
══════════════════════════════════════════════════════════════ */
function SecHead({ icon, title, subtitle }) {
  return (
    <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: "2px solid #f0f4f8" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{
          width: 30, height: 30, borderRadius: 8,
          background: "#eff6ff", color: PRIMARY,
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, flexShrink: 0,
        }}>
          {icon}
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>{title}</div>
          {subtitle && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>{subtitle}</div>}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   MODULE TAB BAR
══════════════════════════════════════════════════════════════ */
const TAB_DEFS = [
  { key: "attendance", label: "Attendance & Workforce", icon: <CalendarOutlined />,          color: "#0e497b", bg: "#dbeafe", border: "#0e497b35" },
  { key: "payroll",    label: "Payroll & Salary",       icon: <MoneyCollectOutlined />,      color: "#059669", bg: "#d1fae5", border: "#05966935" },
  { key: "statutory",  label: "Statutory Compliance",   icon: <SafetyCertificateOutlined />, color: "#d97706", bg: "#fef3c7", border: "#d9770635" },
  { key: "analytics",  label: "HR Analytics",           icon: <BarChartOutlined />,          color: "#7c3aed", bg: "#ede9fe", border: "#7c3aed35" },
  { key: "orgchart",   label: "Org Charts",             icon: <BranchesOutlined />,          color: "#0891b2", bg: "#cffafe", border: "#0891b235" },
];

function ModuleTabs({ active, onChange }) {
  return (
    <div style={{
      display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center",
      background: "rgba(255, 255, 255, 0.95)",
      backdropFilter: "blur(12px)",
      WebkitBackdropFilter: "blur(12px)",
      borderRadius: 14,
      border: "1px solid #e2e8f0", padding: "8px 12px",
      marginBottom: 16, boxShadow: "0 4px 16px rgba(15,23,42,0.08)",
      position: "sticky", top: 12, zIndex: 100,
      transition: "all 0.2s ease",
    }}>
      {TAB_DEFS.map(t => {
        const on = active === t.key;
        return (
          <button
            key={t.key}
            onClick={() => onChange(t.key)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "9px 18px", borderRadius: 10,
              background: on ? t.bg : "transparent",
              border: on ? `1.5px solid ${t.border}` : "1.5px solid transparent",
              color: on ? t.color : "#64748b",
              fontWeight: on ? 700 : 600,
              fontSize: 13, cursor: "pointer", outline: "none",
              transition: "all 0.18s ease", whiteSpace: "nowrap",
              boxShadow: on ? `0 3px 10px ${t.color}22` : "none",
            }}
            onMouseEnter={e => { if (!on) { e.currentTarget.style.background = "#f8fafc"; e.currentTarget.style.color = "#334155"; } }}
            onMouseLeave={e => { if (!on) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#64748b"; } }}
          >
            <span style={{ fontSize: 15, color: on ? t.color : "#94a3b8", display: "flex", alignItems: "center" }}>{t.icon}</span>
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════ */
export default function HrIndex() {
  const { authData, backUrl } = useContext(AuthContext);
  const nav = useNavigation();
  const navigateTo = nav?.navigateTo;

  const [organisations, setOrganisations] = useState([]);
  const [departments,   setDepartments]   = useState([]);
  const [selectedOrg,   setSelectedOrg]   = useState("ALL");
  const [selectedDept,  setSelectedDept]  = useState("ALL");
  const [summaryData,   setSummaryData]   = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [activeTab,     setActiveTab]     = useState("attendance");

  useEffect(() => {
    (async () => {
      try {
        const [o, d] = await Promise.all([
          axios.get(`${backUrl}/api/emp/organisations/`, { headers: { Authorization: `Bearer ${authData?.access}` } }),
          axios.get(`${backUrl}/api/emp/departments/`,   { headers: { Authorization: `Bearer ${authData?.access}` } }),
        ]);
        setOrganisations(o.data?.results || o.data || []);
        setDepartments(d.data?.results   || d.data   || []);
      } catch {}
    })();
  }, [backUrl, authData?.access]);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const p = {};
      if (selectedOrg  !== "ALL") p.organisation_id = selectedOrg;
      if (selectedDept !== "ALL") p.department_id   = selectedDept;
      const res = await axios.get(`${backUrl}/api/emp/employees/dashboard-summary/`, {
        params: p, headers: { Authorization: `Bearer ${authData?.access}` },
      });
      setSummaryData(res.data);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { fetchSummary(); }, [selectedOrg, selectedDept, backUrl, authData?.access]);

  const sd = summaryData;

  const renderTab = () => {
    if (activeTab === "attendance") return (
      <>
        <SecHead icon={<CalendarOutlined />} title="Monthly Attendance & Leave Overview" subtitle="Trends and utilization at a glance" />
        <Row gutter={[16, 16]} style={{ marginBottom: 28 }}>
          <Col xs={24} xl={12}><AttendanceReport /></Col>
          <Col xs={24} xl={12}><LeaveUtilization /></Col>
        </Row>

        <SecHead icon={<TeamOutlined />} title="Employee Daily Attendance Breakdown" />
        <Row gutter={[16, 16]} style={{ marginBottom: 28 }}>
          <Col xs={24}><EmployeeAttendance /></Col>
        </Row>

        <SecHead icon={<ApartmentOutlined />} title="Workforce & Organisation Distribution" />
        <Row gutter={[16, 16]} style={{ marginBottom: 28 }}>
          <Col xs={24} xl={14}><OrganisationLeave /></Col>
          <Col xs={24} xl={10}><TotalEmployees organisationId={selectedOrg} /></Col>
        </Row>

        <SecHead icon={<RiseOutlined />} title="Overtime & Advance Deductions" subtitle="Monthly OT payments and advance deductions by employee" />
        <Row gutter={[16, 16]} style={{ marginBottom: 28 }}>
          <Col xs={24}><OtChart /></Col>
        </Row>

        <SecHead icon={<BarChartOutlined />} title="Department Attendance Benchmark" subtitle="All departments sorted by attendance compliance rate" />
        <Row gutter={[16, 16]} style={{ marginBottom: 28 }}>
          <Col xs={24}><DepartmentAttendanceBenchmark /></Col>
        </Row>

        <SecHead icon={<DollarOutlined />} title="Loss of Pay & Salary Deductions" />
        <Row gutter={[16, 16]}>
          <Col xs={24}><LopChart /></Col>
        </Row>
      </>
    );

    if (activeTab === "payroll") return (
      <>
        <SecHead icon={<MoneyCollectOutlined />} title="Payroll & Salary Analytics" subtitle="Monthly cost · Department distribution" />
        <Row gutter={[16, 16]}>
          <Col xs={24} xl={12}><PayrollSummary /></Col>
          <Col xs={24} xl={12}><DepartmentWiseSalary /></Col>
        </Row>
      </>
    );

    if (activeTab === "statutory") return (
      <>
        <SecHead icon={<SafetyCertificateOutlined />} title="Statutory Compliance" subtitle="PF · ESI · PT · TDS filing status" />
        <Row gutter={[16, 16]}>
          <Col xs={24}><StatutoryCompliance /></Col>
        </Row>
      </>
    );

    if (activeTab === "analytics") return (
      <>
        <SecHead icon={<BarChartOutlined />} title="HR Analytics & Intelligence" />
        <Row gutter={[16, 16]}>
          <Col xs={24}><HrAnalytics /></Col>
        </Row>
      </>
    );

    if (activeTab === "orgchart") return <div style={{ paddingTop: 4 }}><OrgChartList /></div>;
    return null;
  };

  return (
    <div style={{ background: "transparent", padding: "0 0 24px" }}>

      {/* ════ HEADER ════════════════════════════════════════════ */}
      <div style={{
        background: "#fff", borderRadius: 14,
        border: "1px solid #e2e8f0", marginBottom: 14,
        overflow: "hidden", boxShadow: "0 2px 8px rgba(15,23,42,0.06)",
      }}>
        <div style={{ height: 3, background: `linear-gradient(90deg, ${PRIMARY}, #3b82f6)` }} />

        <div style={{ padding: "14px 20px", borderBottom: "1px solid #f0f4f8" }}>
          <Row align="middle" gutter={[12, 12]}>
            <Col xs={24} md={10}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{
                  width: 42, height: 42, borderRadius: 11,
                  background: `linear-gradient(135deg, ${PRIMARY}, #1a6db8)`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#fff", fontSize: 19, flexShrink: 0,
                  boxShadow: "0 4px 14px rgba(14,73,123,0.28)",
                }}>
                  <TeamOutlined />
                </div>
                <div>
                  <div style={{ fontSize: 17, fontWeight: 800, color: "#0f172a", lineHeight: 1.25 }}>
                    Executive HR Dashboard
                  </div>
                  <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>
                    Workforce · Attendance · Payroll · Compliance
                  </div>
                </div>
              </div>
            </Col>
            <Col xs={24} md={14}>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap", alignItems: "center" }}>
                <Select value={selectedOrg} onChange={setSelectedOrg} style={{ width: 200 }}>
                  <Option value="ALL">All Organisations ({organisations.length})</Option>
                  {organisations.map(o => <Option key={o.id} value={o.id}>{o.name}</Option>)}
                </Select>
                <Select value={selectedDept} onChange={setSelectedDept} style={{ width: 190 }}>
                  <Option value="ALL">All Departments ({departments.length})</Option>
                  {departments.map(d => <Option key={d.id} value={d.id}>{d.name}</Option>)}
                </Select>
                <Button icon={<ReloadOutlined />} onClick={fetchSummary} loading={loading} style={{ borderRadius: 8, fontWeight: 600, display: "inline-flex", alignItems: "center" }}>
                  Refresh
                </Button>
              </div>
            </Col>
          </Row>
        </div>

        {/* Quick approvals */}
        <div style={{ padding: "9px 20px", background: "#fafbfc", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginRight: 4, flexShrink: 0 }}>
            Quick Approvals:
          </span>
          <QuickBtn icon={<CalendarOutlined />}    label="Leaves"       color={PRIMARY}   onClick={() => navigateTo && navigateTo("empleaves")} />
          <QuickBtn icon={<FileProtectOutlined />} label="Permissions"  color="#2563eb"   onClick={() => navigateTo && navigateTo("emppermissions")} />
          <QuickBtn icon={<ScheduleOutlined />}    label="Off Day Work" color="#d97706"   onClick={() => navigateTo && navigateTo("empcompoffs")} />
          <QuickBtn icon={<ClockCircleOutlined />} label="On-Duty"      color="#059669"   onClick={() => navigateTo && navigateTo("onduty")} />
        </div>
      </div>

      {/* ════ KPI CARDS ═════════════════════════════════════════ */}
      <Row gutter={[14, 14]} style={{ marginBottom: 14 }}>

        {/* Card 1 — Total Active Staff */}
        <Col xs={24} sm={12} xl={6}>
          <KpiCard
            accent="#0e497b"
            icon={<UsergroupAddOutlined />}
            label="Total Active Staff"
            bigValue={sd?.total_employees || 0}
            loading={loading}
            s1Val={sd?.gender_summary?.male || 0}   s1Label="Male"   s1Color="#2563eb"   s1Icon={<ManOutlined style={{ fontSize: 14 }} />}
            s2Val={sd?.gender_summary?.female || 0}  s2Label="Female" s2Color="#ec4899"   s2Icon={<WomanOutlined style={{ fontSize: 14 }} />}
          />
        </Col>

        {/* Card 2 — Today's Attendance */}
        <Col xs={24} sm={12} xl={6}>
          <KpiCard
            accent="#059669"       iconBg="#d1fae5"
            icon={<CheckCircleOutlined />}
            label="Today's Attendance"
            bigValue={`${sd?.today_attendance?.attendance_rate || 0}%`}
            loading={loading}
            s1Val={sd?.today_attendance?.present || 0}  s1Label="Present" s1Color="#15803d" s1Icon="✓"
            s2Val={sd?.today_attendance?.late || 0}     s2Label="Late"    s2Color="#b45309" s2Icon="⏱"
          />
        </Col>

        {/* Card 3 — On Leave Today */}
        <Col xs={24} sm={12} xl={6}>
          <KpiCard
            accent="#dc2626"
            icon={<CalendarOutlined />}
            label="On Leave Today"
            bigValue={sd?.today_leave_count || 0}
            loading={loading}
            s1Val={sd?.today_full_day_leave_count ?? (sd?.today_leave_count || 0)} s1Label="Full Day" s1Color="#991b1b" s1Icon="📋"
            s2Val={sd?.today_half_day_leave_count ?? 0}                             s2Label="Half Day" s2Color="#d97706" s2Icon="🌓"
          />
        </Col>

        {/* Card 4 — Permissions Today */}
        <Col xs={24} sm={12} xl={6}>
          <KpiCard
            accent="#7c3aed"
            icon={<FileProtectOutlined />}
            label="Permissions Today"
            bigValue={sd?.today_permission_count || 0}
            loading={loading}
            s1Val={sd?.today_permission_fn_count ?? 0} s1Label="Morning"   s1Color="#2563eb" s1Icon="🌅"
            s2Val={sd?.today_permission_an_count ?? 0} s2Label="Afternoon" s2Color="#d97706" s2Icon="☀️"
          />
        </Col>
      </Row>

      {/* ════ MODULE TABS ════════════════════════════════════════ */}
      <ModuleTabs active={activeTab} onChange={setActiveTab} />

      {/* ════ TAB CONTENT ════════════════════════════════════════ */}
      <div style={{
        background: "#fff", border: "1px solid #e2e8f0",
        borderRadius: 12, padding: "20px 22px 28px",
        boxShadow: "0 1px 4px rgba(15,23,42,0.04)", minHeight: 200,
      }}>
        {renderTab()}
      </div>
    </div>
  );
}
